import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import { useLicense } from '../contexts/LicenseContext';
import './TrialLicenseModal.css';

const TrialLicenseModal = ({ isOpen, onClose, onTrialActivated }) => {
  const { activateTrial, refreshLicenseStatus } = useLicense();
  const [systemId, setSystemId] = useState('');
  const [canActivate, setCanActivate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Fixed trial duration set by admin (not user choice)
  const trialDuration = 7; // This should come from admin settings

  useEffect(() => {
    const checkTrialEligibility = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const id = apiService.getStoredSystemId() || apiService.generateSystemId();
        setSystemId(id);
        
        const eligible = await apiService.canUseTrial();
        setCanActivate(eligible);
      } catch (err) {
        console.error('Error checking trial eligibility:', err);
        setError('Failed to check trial eligibility.');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      checkTrialEligibility();
    }
  }, [isOpen]);

  const handleActivateTrial = async () => {
    setError(null);
    setIsLoading(true);
    try {
      console.log('🎯 TrialLicenseModal: Starting trial activation via context...');
      // Use the context's activateTrial function which handles state updates properly
      const result = await activateTrial(trialDuration);
      
      if (result.success && result.trialLicense) {
        console.log('✅ TrialLicenseModal: Trial activated successfully', {
          trialId: result.trialLicense.id,
          systemId: result.trialLicense.systemId
        });
        
        // Call the callback if provided
        if (onTrialActivated) {
          onTrialActivated(result.trialLicense);
        }
        
        // Close the modal
        onClose();
      } else {
        throw new Error(result.error || 'Failed to activate trial');
      }
    } catch (err) {
      console.error('❌ TrialLicenseModal: Error activating trial', err);
      setError(err.message || 'Failed to activate trial.');
      // Don't close modal on error - let user see the error and retry
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="trial-modal-overlay">
      <div className="trial-modal-content">
        <button className="trial-modal-close-btn" onClick={onClose}>×</button>
        <h2 className="trial-modal-title">🚀 Activate Your Free Trial!</h2>
        <p className="trial-modal-description">
          Experience the full power of Fiddyscript AI Content Assistant for 7 days, completely free.
        </p>

        <div className="trial-features">
          <h3>What's Included:</h3>
          <ul>
            <li>✅ Unlimited Humanizations (up to 100 uses)</li>
            <li>✅ Content Editor Access (up to 50 scripts)</li>
            <li>✅ Work Plan Management</li>
            <li>✅ Text to Speech</li>
            <li>✅ All Core Features</li>
          </ul>
        </div>

        {/* Trial duration is fixed by admin - no user choice */}

        <div className="trial-info-section">
          <p>Your System ID: <code>{systemId || 'Generating...'}</code></p>
          {isLoading ? (
            <p>Checking eligibility...</p>
          ) : (
            canActivate ? (
              <p className="eligible-message">🎉 You are eligible for a free trial!</p>
            ) : (
              <p className="ineligible-message">
                🚫 This system has already used a free trial. Please purchase a full license to continue.
              </p>
            )
          )}
          {error && <p className="trial-error-message">{error}</p>}
        </div>

        <div className="trial-actions">
          <button
            className="activate-trial-btn"
            onClick={handleActivateTrial}
            disabled={!canActivate || isLoading}
          >
            {isLoading ? 'Activating...' : 'Activate Free Trial'}
          </button>
          <button className="learn-more-btn" onClick={onClose}>I have a license already</button>
        </div>

        <div className="trial-upgrade-link">
          <a href="https://fiddyscript.com" target="_blank" rel="noopener noreferrer">
            Upgrade or Buy a license
          </a>
        </div>

        <p className="trial-disclaimer">
          *Trial is limited to one activation per system ID to prevent abuse.
        </p>
      </div>
    </div>
  );
};

export default TrialLicenseModal;

